import React, { useState, useEffect } from "react";
import { Button, Input, Modal, Tag, TreeSelect, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  CrownOutlined,
} from "@ant-design/icons";
import { staffAPIWithErrorHandling, staffHelpers } from "../../../api/staff/staff";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import StaffFormDrawer from "./StaffFormDrawer";
import StaffDetailDrawer from "./StaffDetailDrawer";

// const DEFAULT_VISIBLE_FILTERS = ["search", "organization"];
const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["code", "phone", "dob", "organization", "role", "status"];

const ROLE_CONFIG = {
  BRANCH_MANAGER: { text: "Quản lý", icon: <CrownOutlined style={{ color: "#A8681E" }} /> },
  WARD_LEADER: { text: "Quản lý cấp phường", icon: <CrownOutlined style={{ color: "#656DC7" }} /> },
  STAFF: { text: "Nhân viên", icon: <UserOutlined style={{ color: "#0070D7" }} /> },
};

export default function StaffTable() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    from: 0,
    to: 0,
    lastPage: 1,
  });
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    sort_by: "created_at",
    sort_order: "desc",
  });

  const [treeData, setTreeData] = useState([]);
  const [valueOrg, setValueOrg] = useState();

  // Password Modal states
  // Thêm mới / chỉnh sửa / xem chi tiết đều làm bằng Drawer ngay trên danh sách
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const flattenTreeData = (nodes, parentId = 0) => {
    let result = [];
    nodes.forEach((node) => {
      result.push({
        id: node.id,
        pId: parentId,
        value: String(node.id),
        title: node.name,
        isLeaf: node.children_recursive?.length === 0,
      });

      if (node.children_recursive && node.children_recursive.length > 0) {
        result = result.concat(flattenTreeData(node.children_recursive, node.id));
      }
    });
    return result;
  };

  // Load Organizations for select options
  const loadOrganizations = async () => {
    try {
      const response = await organizationAPIWithErrorHandling.getTreeStructure();
      if (response.success) {
        const rawData = response.data;
        const flatTree = flattenTreeData(rawData);
        setTreeData(flatTree); // Dùng cho TreeSelect
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  useEffect(() => {
    loadOrganizations();
    fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeOrg = (newValue) => {
    setValueOrg(newValue);
    fetchStaff(1, pagination.pageSize, searchText, newValue, filters.status);
  };

  // Fetch staff data using the proper API
  const fetchStaff = async (
    page = 1,
    perPage = 10,
    search = "",
    valueOrg = "",
    status = ""
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: search.trim(),
        orgId: valueOrg,
        status,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      };

      const response = await staffAPIWithErrorHandling.getStaffs(params);
      const { staffs: customerData, pagination: paginationData } =
        staffHelpers.extractStaffList(response);

      setCustomers(customerData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
        from: paginationData.from,
        to: paginationData.to,
        lastPage: paginationData.lastPage,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách nhân sự");
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle table pagination change
  const handleTableChange = (paginationConfig, tableFilters, sorter) => {
    if (sorter && sorter.field) {
      const sortBy = sorter.field;
      const sortOrder = sorter.order === "descend" ? "desc" : "asc";
      setFilters((prev) => ({ ...prev, sort_by: sortBy, sort_order: sortOrder }));
    }

    let statusFilter = "";
    if (tableFilters && tableFilters.status && tableFilters.status.length > 0) {
      statusFilter = tableFilters.status[0] ? "active" : "inactive";
    }

    fetchStaff(
      paginationConfig.current,
      paginationConfig.pageSize,
      searchText,
      valueOrg,
      statusFilter
    );
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
    fetchStaff(1, pagination.pageSize, value, valueOrg, filters.status);
  };

  // Xoá toàn bộ bộ lọc
  const handleReset = () => {
    setSearchText("");
    setValueOrg(undefined);
    fetchStaff(1, pagination.pageSize, "", "", "");
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Action handlers
  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      message.error("Mật khẩu phải có ít nhất 6 ký tự!");
      return;
    }

    setPasswordLoading(true);
    try {
      await staffAPIWithErrorHandling.updateStaffPassword(selectedStaff.id, newPassword);
      message.success(`Đã đổi mật khẩu cho nhân sự ${selectedStaff.name} thành công!`);
      setIsPasswordModalVisible(false);
      setNewPassword("");
      setSelectedStaff(null);
    } catch (error) {
      message.error(error.message || "Không thể đổi mật khẩu");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleInfo = (record) => {
    setDetailId(record.id);
    setDetailOpen(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingRecord(null);
  };

  // Từ drawer chi tiết bấm "Chỉnh sửa" -> đóng chi tiết, mở form ngay
  const handleEditFromDetail = (staff) => {
    setDetailOpen(false);
    setEditingRecord(staff);
    setFormOpen(true);
  };

  const handleDelete = async (record) => {
    try {
      const confirmed = await confirmDelete({ entity: "nhân sự", name: record.name });
      if (!confirmed) return;

      await staffAPIWithErrorHandling.deleteStaff(record.id);
      message.success("Xóa nhân sự thành công");

      fetchStaff(pagination.current, pagination.pageSize, searchText, filters.status);
    } catch (error) {
      message.error(error.message || "Không thể xóa nhân sự");
    }
  };

  const handleAdd = () => {
    setEditingRecord(null);
    setFormOpen(true);
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "index", label: "STT", fixed: true,
      column: {
        title: "STT", key: "index", width: 60, align: "center", fixed: "left",
        render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
      },
    },
    {
      key: "name", label: "Họ tên", fixed: true,
      column: { title: "Họ tên", dataIndex: "name", key: "name", width: 200, fixed: "left", sorter: true },
    },
    {
      key: "code", label: "Mã KH",
      column: {
        title: "Mã KH", dataIndex: "code", key: "code", width: 150,
        render: (text) => <strong className="text-blue-600">{text}</strong>,
      },
    },
    {
      key: "phone", label: "Điện thoại",
      column: { title: "Điện thoại", dataIndex: "phone", key: "phone", width: 120 },
    },
    {
      key: "dob", label: "Ngày sinh",
      column: { title: "Ngày sinh", dataIndex: "dob", key: "dob", width: 120, render: (date) => formatDate(date) },
    },
    {
      key: "organization", label: "Đơn vị",
      column: {
        title: "Đơn vị", key: "organization", width: 220,
        render: (_, record) => {
          // Nhân sự phụ trách nhiều đơn vị: gộp tên, lùi về dạng cũ một đơn vị
          // nếu bản ghi chưa có `organizations`.
          const names = record?.organizations?.length
            ? record.organizations.map((item) => item.name)
            : [record?.staff_org_map?.organization?.name].filter(Boolean);

          return <div title={names.join(", ")}>{names.join(", ") || "-"}</div>;
        },
      },
    },
    {
      key: "role", label: "Vai trò",
      column: {
        title: "Vai trò", key: "role", width: 200,
        render: (_, record) => {
          const type = record?.user?.info?.type;
          const currentRole = ROLE_CONFIG[type] || {
            text: "Không xác định",
            icon: <UserOutlined style={{ color: "#94A3B8" }} />,
          };
          return (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {currentRole.icon}
              {currentRole.text}
            </span>
          );
        },
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 130,
        render: (status) => (
          <Tag color={status ? "green" : "red"}>
            {status ? "Hoạt động" : "Không hoạt động"}
          </Tag>
        ),
        filters: [
          { text: "Hoạt động", value: true },
          { text: "Không hoạt động", value: false },
        ],
        filterMultiple: false,
      },
    },
    actionColumnDef((record) => [
      { key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view", onClick: () => handleInfo(record) },
      { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => handleEdit(record) },
      {
        key: "password", label: "Đổi mật khẩu", icon: <KeyOutlined />, tone: "warning",
        onClick: () => { setSelectedStaff(record); setIsPasswordModalVisible(true); },
      },
      { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
    ]),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm theo tên, email, số điện thoại..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "organization", label: "Đơn vị", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <TreeSelect
          treeDataSimpleMode
          style={{ width: "100%" }}
          value={valueOrg}
          styles={{ popup: { root: { maxHeight: 400, overflow: "auto" } } }}
          placeholder="Chọn đơn vị"
          onChange={onChangeOrg}
          treeData={treeData}
          allowClear
        />
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || valueOrg);

  return (
    <BrandedPageLayout
      title="Nhân sự"
      subtitle="Quản lý danh sách nhân sự theo đơn vị và vai trò."
      breadcrumbItems={[{ title: "Nhân sự" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => fetchStaff(pagination.current, pagination.pageSize, searchText, valueOrg, filters.status)}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Thêm nhân sự
          </Button>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={customers}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có nhân sự"
      />

      <StaffFormDrawer
        open={formOpen}
        record={editingRecord}
        treeData={treeData}
        onClose={handleCloseForm}
        onSaved={() =>
          // Sửa thì ở nguyên trang đang xem, thêm mới thì về trang 1
          fetchStaff(
            editingRecord ? pagination.current : 1,
            pagination.pageSize,
            searchText,
            valueOrg,
            filters.status
          )
        }
      />

      <StaffDetailDrawer
        open={detailOpen}
        staffId={detailId}
        onClose={() => setDetailOpen(false)}
        onEdit={handleEditFromDetail}
      />

      {/* Change Password Modal */}
      <Modal
        title={`Đổi mật khẩu cho: ${selectedStaff?.name}`}
        open={isPasswordModalVisible}
        onOk={handlePasswordChange}
        onCancel={() => {
          setIsPasswordModalVisible(false);
          setNewPassword("");
          setSelectedStaff(null);
        }}
        confirmLoading={passwordLoading}
        okText="Đổi mật khẩu"
        cancelText="Hủy"
      >
        <div className="py-4">
          <p className="mb-2 font-medium">Nhập mật khẩu mới:</p>
          <Input.Password
            placeholder="Nhập ít nhất 6 ký tự"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="rounded-lg"
          />
        </div>
      </Modal>
    </BrandedPageLayout>
  );
}
